import { Link } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { hasAnyRole, ROLES } from '@/auth/roles'
import { useFormat, useI18n, type MessageKey } from '@/i18n'
import { Banner, BilingualLabel, Card, CardBody, CardHeader, EmptyState } from '@/ui'
import type { ProcessState } from '@/features/process/projection'
import { ProcessScreen } from '@/features/process/components/ProcessScreen'
import { cartonSplit, lotState, pouchesLeft } from '../model/reconcile'
import { LotStateBadge } from '../components/LotStateBadge'

/**
 * Pack lots at the hall and what each is waiting for (F7.6 / F7-B1.a seen from the floor): the
 * pouches stay in the quarantine area until a quality person releases the lot; held, recalled or
 * withdrawn lots never leave.
 */
export function PackLotsPage() {
  return <ProcessScreen title="pack.lots.title">{(state) => <PackLots state={state} />}</ProcessScreen>
}

const HOLD_REASONS = ['LAB_FAIL', 'RUN_MIXED', 'LABEL_MISMATCH', 'QUALITY']

function holdReasonKey(reason: string | null | undefined): MessageKey | null {
  return reason && HOLD_REASONS.includes(reason) ? (`pack.holdReason.${reason}` as MessageKey) : null
}

const ORDER = ['HELD', 'RECALLED', 'WAITING', 'PACKING', 'RELEASED', 'WITHDRAWN']

function PackLots({ state }: { state: ProcessState }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const actor = useActor()
  const lots = [...state.packLots.values()].sort(
    (a, b) => ORDER.indexOf(lotState(a)) - ORDER.indexOf(lotState(b)) || b.pack_date.localeCompare(a.pack_date),
  )
  const canDispatch = hasAnyRole(actor.roles, [ROLES.STORE_KEEPER, ROLES.SUPERVISOR])
  const releasedLeft = lots.filter((l) => lotState(l) === 'RELEASED' && pouchesLeft(l) > 0)
  if (!lots.length) return <EmptyState title={t('pack.lots.none')} />

  return (
    <div className="flex flex-col gap-4">
      {canDispatch && releasedLeft.length > 0 && (
        <Banner
          tone="success"
          action={
            <Link to="/m/pack/dispatch" className="font-mono text-[12px] font-bold uppercase underline">
              {t('pack.dispatch.title')}
            </Link>
          }
        >
          {t('pack.lots.readyToGo', { n: releasedLeft.length })}
        </Banner>
      )}
      {lots.map((l) => {
        const s = lotState(l)
        const left = pouchesLeft(l)
        const run = l.run_id ? state.packRuns.get(l.run_id) : undefined
        const perCarton = run?.sku.pouches_per_carton
        const split = l.pouch_count && perCarton ? cartonSplit(l.pouch_count, perCarton) : null
        return (
          <Card key={l.id}>
            <CardHeader
              eyebrow={l.sku_name ?? run?.sku.name}
              title={<span className="font-mono text-[17px]">{l.code}</span>}
              actions={<LotStateBadge lot={l} />}
            />
            <CardBody className="flex flex-col gap-2 pt-0 text-[14px]">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted">
                <span>{t('pack.packDate', { date: fmt.date(l.pack_date) })}</span>
                <span>{t('pack.bestBefore', { date: fmt.date(l.best_before) })}</span>
              </div>
              {l.pouch_count != null && (
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <span>{t('pack.pouchesN', { n: l.pouch_count })}</span>
                  {split && perCarton && (
                    <span className="text-muted">
                      {t('pack.cartons', { full: split.full, per: perCarton, partial: split.partial })}
                    </span>
                  )}
                  {s === 'RELEASED' && <span className="font-bold">{t('pack.pouchesLeft', { n: left })}</span>}
                </div>
              )}
              {s === 'WAITING' && <p className="text-muted"><BilingualLabel k="pack.lots.waitingHint" /></p>}
              {s === 'HELD' && (
                <p className="text-warn">
                  <BilingualLabel k="pack.lots.heldHint" />
                  {holdReasonKey(l.hold_reason) ? ` · ${t(holdReasonKey(l.hold_reason)!)}` : ''}
                </p>
              )}
              {s === 'RECALLED' && <p className="text-danger"><BilingualLabel k="pack.lots.recalledHint" /></p>}
            </CardBody>
          </Card>
        )
      })}
    </div>
  )
}
